<template>
  <dialog
    ref="dialog"
    class="member-dialog m-auto max-h-[90vh] w-[min(92vw,420px)] overflow-hidden rounded-3xl border border-edge bg-panel p-0 text-slate-100 shadow-2xl"
    :aria-label="member ? `${member.firstName}, Trikotnummer ${member.shirtNumber}` : undefined"
    @close="emit('close')"
    @click="onBackdropClick"
  >
    <div v-if="member" class="flex flex-col">
      <div class="relative aspect-square w-full bg-night">
        <img
          :src="memberPhotoUrl(member.id, member.photoVersion)"
          :alt="member.photoVersion ? `Foto von ${member.firstName}` : 'Drunken Hippos Logo'"
          class="h-full w-full"
          :class="member.photoVersion ? 'object-cover' : 'object-contain p-10'"
        />
        <button
          @click="close"
          class="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-xl border border-edge bg-night/80 backdrop-blur hover:border-beer hover:text-beer"
          aria-label="Schließen"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="flex items-center gap-4 p-4 sm:p-5">
        <span
          class="flex h-12 w-12 flex-none items-center justify-center rounded-xl font-display text-xl font-extrabold"
          :style="{ backgroundColor: member.color, color: readableTextOn(member.color) }"
        >
          {{ member.shirtNumber }}
        </span>
        <div class="flex min-w-0 flex-col">
          <span class="truncate font-display text-[22px] font-extrabold">{{ member.firstName }}</span>
          <span class="text-sm text-slate-400">Trikotnummer {{ member.shirtNumber }}</span>
        </div>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import Member from '@/types/Member';
import { memberPhotoUrl } from '@/utils/photo';
import { readableTextOn } from '@/utils/format';

const props = defineProps<{ member: Member | null }>();
const emit = defineEmits<{ close: [] }>();

const dialog = ref<HTMLDialogElement | null>(null);

const close = () => dialog.value?.close();

// Klick auf den abgedunkelten Hintergrund (außerhalb des Inhalts) schließt den Dialog
const onBackdropClick = (event: MouseEvent) => {
  if (event.target === dialog.value) close();
};

watch(
  () => props.member,
  async (member) => {
    await nextTick();
    if (member && !dialog.value?.open) dialog.value?.showModal();
    if (!member && dialog.value?.open) dialog.value.close();
  },
);
</script>

<style scoped>
.member-dialog::backdrop {
  background: rgb(0 0 0 / 0.7);
}

@media (prefers-reduced-motion: no-preference) {
  .member-dialog[open] {
    animation: dialog-in 0.2s ease-out;
  }

  @keyframes dialog-in {
    from {
      opacity: 0;
      transform: translateY(8px) scale(0.97);
    }
  }
}
</style>
